import React, { useEffect, useState } from 'react';
import { Shield, Save, CheckCircle, AlertTriangle } from 'lucide-react';
import { useApi } from './DashboardLayout';

export default function SecurityPage() {
  const [config, setConfig] = useState({
    session_timeout_minutes: 480,
    max_login_attempts: 5,
    lockout_minutes: 15,
    password_min_length: 8,
    password_require_uppercase: true,
    password_require_lowercase: true,
    password_require_number: true,
    password_require_special: true,
    initial_password: 'BgprPortal2026!',
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const { apiFetch } = useApi();

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    const r = await apiFetch('/api/settings/security');
    if (!r) return;
    try { const d = await r.json(); if (d) setConfig(prev => ({ ...prev, ...d })); } catch {}
  };

  const showFb = (type, text) => { setFeedback({ type, text }); setTimeout(() => setFeedback(null), 5000); };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const r = await apiFetch('/api/settings/security', { method: 'PUT', body: JSON.stringify(config) });
      if (!r) { setSaving(false); return; }
      const d = await r.json();
      if (d?.status) showFb('success', 'Sicherheitseinstellungen gespeichert.');
      else showFb('error', d?.detail || 'Fehler beim Speichern.');
    } catch (e) {
      showFb('error', 'Fehler: ' + e.message);
    }
    setSaving(false);
  };

  const set = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const timeoutOptions = [
    { label: '15 Minuten', value: 15 },
    { label: '30 Minuten', value: 30 },
    { label: '1 Stunde', value: 60 },
    { label: '2 Stunden', value: 120 },
    { label: '4 Stunden', value: 240 },
    { label: '8 Stunden', value: 480 },
    { label: '24 Stunden', value: 1440 },
  ];

  return (
    <div data-testid="security-page">
      <div className="page-header">
        <h2>Einstellungen</h2>
        <p>Sicherheit</p>
      </div>

      {feedback && (
        <div className={`design-feedback ${feedback.type}`} data-testid="security-feedback">
          {feedback.type === 'success' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
          {feedback.text}
        </div>
      )}

      {/* Session Timeout */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Shield size={20} style={{ color: 'var(--text-secondary)' }} />
            <h4 style={{ fontSize: 16, fontWeight: 700 }}>Sitzungs-Timeout</h4>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
            Nach dieser Inaktivitätsdauer werden Benutzer automatisch abgemeldet.
          </p>
          <div style={{ maxWidth: 320 }}>
            <label className="smtp-label">Timeout</label>
            <select className="form-input" value={config.session_timeout_minutes} onChange={e => set('session_timeout_minutes', parseInt(e.target.value))} data-testid="security-timeout-select">
              {timeoutOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Rate Limiting */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Shield size={20} style={{ color: 'var(--text-secondary)' }} />
            <h4 style={{ fontSize: 16, fontWeight: 700 }}>Brute-Force-Schutz</h4>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
            Schutz gegen wiederholte Anmeldeversuche mit falschen Passwörtern.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxWidth: 500 }}>
            <div>
              <label className="smtp-label">Max. Fehlversuche</label>
              <input className="form-input" type="number" min={2} max={20} value={config.max_login_attempts} onChange={e => set('max_login_attempts', parseInt(e.target.value) || 5)} data-testid="security-max-attempts" />
            </div>
            <div>
              <label className="smtp-label">Sperrzeit (Minuten)</label>
              <input className="form-input" type="number" min={1} max={60} value={config.lockout_minutes} onChange={e => set('lockout_minutes', parseInt(e.target.value) || 15)} data-testid="security-lockout-minutes" />
            </div>
          </div>
        </div>
      </div>

      {/* Password Policy */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Shield size={20} style={{ color: 'var(--text-secondary)' }} />
            <h4 style={{ fontSize: 16, fontWeight: 700 }}>Passwort-Richtlinien</h4>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20 }}>
            Anforderungen für neue Passwörter bei allen Benutzern.
          </p>
          <div style={{ maxWidth: 300, marginBottom: 16 }}>
            <label className="smtp-label">Mindestlänge</label>
            <input className="form-input" type="number" min={6} max={32} value={config.password_min_length} onChange={e => set('password_min_length', parseInt(e.target.value) || 8)} data-testid="security-min-length" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { key: 'password_require_uppercase', label: 'Großbuchstaben erforderlich (A-Z)' },
              { key: 'password_require_lowercase', label: 'Kleinbuchstaben erforderlich (a-z)' },
              { key: 'password_require_number', label: 'Zahl erforderlich (0-9)' },
              { key: 'password_require_special', label: 'Sonderzeichen erforderlich (!@#$...)' },
            ].map(item => (
              <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={config[item.key]} onChange={e => set(item.key, e.target.checked)} style={{ accentColor: 'var(--primary)' }} data-testid={`security-${item.key}`} />
                {item.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Initial Password */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Shield size={20} style={{ color: 'var(--text-secondary)' }} />
            <h4 style={{ fontSize: 16, fontWeight: 700 }}>Initialpasswort</h4>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Dieses Passwort wird neuen Anbietern als Erstpasswort zugewiesen und kann in der Erstinfo-Mail mitgeteilt werden. Anbieter werden aufgefordert, es nach dem ersten Login zu ändern.
          </p>
          <div style={{ maxWidth: 320 }}>
            <label className="smtp-label">Initialpasswort</label>
            <input className="form-input" value={config.initial_password} onChange={e => set('initial_password', e.target.value)} data-testid="security-initial-password" />
          </div>
        </div>
      </div>

      <button className="btn btn-primary" onClick={saveConfig} disabled={saving} data-testid="security-save-btn">
        <Save size={14} /> Speichern
      </button>
    </div>
  );
}
